import {
  faExclamationCircle,
  faInfoCircle,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type {
  ComponentPropsWithoutRef,
  FC,
  ReactElement,
  ReactNode,
} from 'react';
import {
  CircleAlertIcon,
  CircleXIcon,
  InfoIcon,
} from 'src/components/media-and-icons/icons';
import { Heading } from 'src/components/text/heading';
import { useIsNewDesignActive } from 'src/pages/new-design/is-new-design';
import { cn } from 'src/utils/cn';

type AlertProps = {
  type: 'info' | 'warning' | 'error';
  title?: ReactNode;
} & ComponentPropsWithoutRef<'div'>;

const label: Record<AlertProps['type'], string> = {
  warning: 'Warning',
  error: 'Error',
  info: 'Info',
};

const newIcons: Record<AlertProps['type'], ReactElement> = {
  info: <InfoIcon size="md" />,
  warning: <CircleAlertIcon size="md" />,
  error: <CircleXIcon size="md" />,
};

const newClassNames: Record<AlertProps['type'], string> = {
  info: cn(`bg-background-support-info-minimal text-foreground-support-info`),
  warning: cn(
    `bg-background-support-warning-minimal text-foreground-support-warning`
  ),
  error: cn(
    `bg-background-support-error-minimal text-foreground-support-error`
  ),
};

const LegacyAlert: FC<AlertProps> = ({ type, children, title, ...rest }) => {
  return (
    <div
      className={cn(
        `p-xl`,
        type === 'info' && `bg-legacy-brand-light-4 text-legacy-brand`,
        type === 'warning' && `legacy:bg-[#fcf8e3] legacy:text-[#8a6d3b]`,
        type === 'error' && `bg-legacy-error-lighter text-legacy-error`
      )}
      data-testid={`alert-${type}`}
      {...rest}
    >
      {title ? (
        <div className={cn(`grid items-center gap-md`, `legacy:gap-[1rem]`)}>
          <Heading mode="title" size="md">
            <FontAwesomeIcon
              icon={type === 'info' ? faInfoCircle : faExclamationCircle}
              aria-hidden
            />{' '}
            {title}
          </Heading>
          <div>{children}</div>
        </div>
      ) : (
        <>
          <FontAwesomeIcon
            icon={type === 'info' ? faInfoCircle : faExclamationCircle}
            aria-hidden
          />{' '}
          <strong>{label[type]}</strong> {children}
        </>
      )}
    </div>
  );
};

const NewAlert: FC<AlertProps> = ({ type, children, title, ...rest }) => {
  return (
    <div
      className={cn(
        newClassNames[type],
        `grid grid-cols-[auto_1fr] gap-lg rounded-xs px-xl py-lg`
      )}
      data-testid={`alert-${type}`}
      {...rest}
    >
      {newIcons[type]}

      <div className="grid gap-sm self-center">
        {title && <div className="font-body-bold text-inherit!">{title}</div>}

        <div>{children}</div>
      </div>
    </div>
  );
};

export const Alert: FC<AlertProps> = (props) => {
  const isNewDesignActive = useIsNewDesignActive();

  return isNewDesignActive ? (
    <NewAlert {...props} />
  ) : (
    <LegacyAlert {...props} />
  );
};
